import { UserPreferences } from '@/app/features/authentication/services/auth.service';
import { Injectable, effect, signal, computed } from '@angular/core';

import {
    $t,
    updatePreset,
    updateSurfacePalette
} from '@primeuix/themes';

import Aura from '@primeuix/themes/aura';
import Lara from '@primeuix/themes/lara';
import Nora from '@primeuix/themes/nora';

const presets = {
    Aura,
    Lara,
    Nora,
} as const;

export interface LayoutConfig {
    preset: string;
    primary: string;
    surface: string | undefined | null;
    darkTheme: boolean;
    menuMode: string;
}

interface LayoutState {
    staticMenuDesktopInactive: boolean;
    overlayMenuActive: boolean;
    configSidebarVisible: boolean;
    mobileMenuActive: boolean;
    menuHoverActive: boolean;
    activePath: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    layoutConfig = signal<LayoutConfig>({
        preset: 'Aura',
        primary: 'blue',
        surface: null,
        darkTheme: false,
        menuMode: 'static'
    });

    layoutState = signal<LayoutState>({
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: false,
        mobileMenuActive: false,
        menuHoverActive: false,
        activePath: null
    });

    theme = computed(() => (this.layoutConfig().darkTheme ? 'light' : 'dark'));

    isSidebarActive = computed(() => this.layoutState().overlayMenuActive || this.layoutState().mobileMenuActive);

    isDarkTheme = computed(() => this.layoutConfig().darkTheme);

    getPrimary = computed(() => this.layoutConfig().primary);

    getSurface = computed(() => this.layoutConfig().surface);

    isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

    transitionComplete = signal<boolean>(false);

    private initialized = false;

    constructor() {
        effect(() => {
            const config = this.layoutConfig();

            if (!this.initialized || !config) {
                this.initialized = true;
                return;
            }

            this.handleDarkModeTransition(config);
        });
    }

    private handleDarkModeTransition(config: LayoutConfig): void {
        const supportsViewTransition = 'startViewTransition' in document;

        if (supportsViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode(config);
        }
    }

    private startViewTransition(config: LayoutConfig): void {
        document.startViewTransition(() => {
            this.toggleDarkMode(config);
        });
    }

    setPreset(preset: string): void {
        if (!(preset in presets)) {
            return;
        }

        this.layoutConfig.update(state => ({
            ...state,
            preset,
        }));

        const selectedPreset =
            presets[preset as keyof typeof presets];

        $t()
            .preset(selectedPreset)
            .use({
                useDefaultOptions: true,
            });
    }
    setPrimary(primary: string): void {

        this.layoutConfig.update(state => ({
            ...state,
            primary,
        }));

        updatePreset({
            semantic: {
                primary: {
                    50: `{${primary}.50}`,
                    100: `{${primary}.100}`,
                    200: `{${primary}.200}`,
                    300: `{${primary}.300}`,
                    400: `{${primary}.400}`,
                    500: `{${primary}.500}`,
                    600: `{${primary}.600}`,
                    700: `{${primary}.700}`,
                    800: `{${primary}.800}`,
                    900: `{${primary}.900}`,
                    950: `{${primary}.950}`,
                },
            },
        });

    }

    applyTheme(): void {
        const config = this.layoutConfig();

        const preset =
            presets[config.preset as keyof typeof presets];

        if (!preset) {
            return;
        }

        $t()
            .preset(preset)
            .use({
                useDefaultOptions: true
            });
    }

    toggleDarkMode(config?: LayoutConfig): void {
        const _config = config || this.layoutConfig();
        if (_config.darkTheme) {
            document.documentElement.classList.add('app-dark');
        } else {
            document.documentElement.classList.remove('app-dark');
        }
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !this.layoutState().overlayMenuActive }));
        }

        if (this.isDesktop()) {
            this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive }));
        } else {
            this.layoutState.update((prev) => ({ ...prev, mobileMenuActive: !this.layoutState().mobileMenuActive }));
        }
    }

    showConfigSidebar() {
        this.layoutState.update((prev) => ({ ...prev, configSidebarVisible: true }));
    }

    hideConfigSidebar() {
        this.layoutState.update((prev) => ({ ...prev, configSidebarVisible: false }));
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return !this.isDesktop();
    }

    applyPreferences(preferences: UserPreferences): void {
        const primary = preferences.primary ?? 'blue';
        const surface = preferences.surface ?? null;
        const preset = preferences.theme ?? 'Aura';
        const darkTheme = preferences.color_scheme === 'DARK';

        this.layoutConfig.set({
            ...this.layoutConfig(),
            primary,
            surface,
            preset,
            darkTheme,
        });

        // 1. Primijeni preset
        this.setPreset(preset);

        // 2. ODMAH nakon preseta primijeni primary
        this.setPrimary(primary);



        // 4. Dark/light
        this.toggleDarkMode(
            this.layoutConfig()
        );
    }

    
}
